import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { supabase, edgeFnUrl } from '@saas-boilerplate/shared';
import type { Session, User, AuthError } from '@supabase/supabase-js';
import { AuthState, getViewForState, getNextState } from '../lib/auth-state-machine';
import { getPendingOAuthInvite } from '../components/accept-invitation';
import {
  lastUsedLoginTypeFromUser,
  setLastUsedLoginType,
} from '../lib/last-used-login-type';
import { stashOAuthProviderTokens } from '../lib/calendar-connection';
import { identifyUser, resetAnalytics } from '../lib/analytics';

// Safety net only. With the AAL check deferred out of the onAuthStateChange callback (see below),
// getAuthenticatorAssuranceLevel() resolves in milliseconds, so this timeout should never fire in
// practice - it just stops a genuinely-hung MFA call from blocking boot indefinitely.
const MFA_CHECK_TIMEOUT_MS = 3000;

/** Edge functions often return { error, details } - surface details (e.g. Resend API message) for setup/debugging. */
function formatEdgeFunctionError(result: { error?: string; details?: string }): string {
  const base = result.error || 'Request failed';
  const d = result.details;
  if (typeof d === 'string' && d.trim()) {
    return `${base} - ${d.trim()}`;
  }
  return base;
}

async function getAalWithTimeout() {
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error('MFA check timeout')), MFA_CHECK_TIMEOUT_MS)
  );
  const { data } = await Promise.race([
    supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
    timeout,
  ]);
  return data;
}

function isMfaTimeoutError(err: unknown): boolean {
  return err instanceof Error && err.message === 'MFA check timeout';
}

function getAppUrl(): string {
  const configured = import.meta.env.VITE_APP_URL?.trim();
  if (configured) return configured.replace(/\/$/, '');
  return window.location.origin;
}

export interface AuthContextType {
  session: Session | null;
  user: User | null;
  authState: AuthState;
  isLoading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
  setAuthState: (state: AuthState) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [authState, setAuthState] = useState<AuthState>('initializing');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Analytics identity: tie events to the Supabase user id; clear on sign-out so the several
  // humans here who run multiple accounts are never merged into one person.
  useEffect(() => {
    if (user?.id) {
      identifyUser(user.id, {
        email: user.email ?? null,
        name: (user.user_metadata as { full_name?: string } | null)?.full_name ?? null,
      });
    } else {
      resetAnalytics();
    }
  }, [user?.id]);

  // Initialize: Check for existing session and handle OAuth callbacks
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        // Check for OAuth / invite tokens in URL hash first
        const hash = window.location.hash;
        if (hash && (hash.includes('access_token') || hash.includes('refresh_token'))) {
          const hashParams = new URLSearchParams(hash.substring(1));
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');
          const type = hashParams.get('type');

          if (accessToken && refreshToken) {
            // Set session from OAuth tokens
            const { data: { session: oauthSession }, error: oauthError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            if (!mounted) return;

            if (oauthError) {
              console.error('Error setting session from OAuth:', oauthError);
              setError(oauthError.message);
              setAuthState('unauthenticated');
              setIsLoading(false);
              return;
            }

            if (oauthSession) {
              setSession(oauthSession);
              setUser(oauthSession.user);

              // Clean up URL
              const cleanedHash = new URLSearchParams(hash.substring(1));
              cleanedHash.delete('access_token');
              cleanedHash.delete('refresh_token');
              cleanedHash.delete('expires_in');
              cleanedHash.delete('expires_at');
              cleanedHash.delete('token_type');
              const remainingHash = cleanedHash.toString();
              const newUrl = window.location.pathname + window.location.search + (remainingHash ? `#${remainingHash}` : '');
              window.history.replaceState({}, document.title, newUrl);

              // After setting session from tokens:
              // - For invite flows, go straight to accepting_invitation view
              // - For recovery flows, the PasswordReset component is shown based on hash
              // - For OAuth returns with a pending invite in localStorage, go to accepting_invitation
              // - Otherwise, proceed to checking invitations
              if (type === 'invite') {
                setAuthState('accepting_invitation');
              } else if (getPendingOAuthInvite()) {
                setAuthState('accepting_invitation');
              } else {
                try {
                  const aalData = await getAalWithTimeout();
                  if (aalData?.nextLevel === 'aal2' && aalData?.currentLevel === 'aal1') {
                    setAuthState('mfa_challenge');
                  } else {
                    setAuthState('checking_invitations');
                  }
                } catch (e) {
                  if (!isMfaTimeoutError(e)) {
                    console.error('Error checking MFA level:', e);
                  }
                  setAuthState('checking_invitations');
                }
              }
              setIsLoading(false);
              return;
            }
          }
        }

        // No OAuth tokens, check for existing session
        const { data: { session: initialSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (!mounted) return;

        if (sessionError) {
          console.error('Error getting session:', sessionError);
          setError(sessionError.message);
          setAuthState('unauthenticated');
          setIsLoading(false);
          return;
        }

        if (initialSession) {
          setSession(initialSession);
          setUser(initialSession.user);
          // Check if MFA verification is needed before proceeding
          try {
            const aalData = await getAalWithTimeout();
            if (aalData?.nextLevel === 'aal2' && aalData?.currentLevel === 'aal1') {
              setAuthState('mfa_challenge');
            } else {
              setAuthState('checking_invitations');
            }
          } catch (e) {
            if (!isMfaTimeoutError(e)) {
              console.error('Error checking MFA level:', e);
            }
            setAuthState('checking_invitations');
          }
        } else {
          setAuthState('unauthenticated');
        }
      } catch (err) {
        console.error('Error initializing auth:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize authentication');
        setAuthState('unauthenticated');
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        if (!mounted) return;

        console.log('Auth state changed:', event, newSession?.user?.email);

        setSession(newSession);
        setUser(newSession?.user ?? null);
        setError(null);

        if (
          newSession?.user &&
          (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')
        ) {
          const loginType = lastUsedLoginTypeFromUser(newSession.user);
          if (loginType) setLastUsedLoginType(loginType);
          // Capture the Google/Microsoft provider refresh token right after the OAuth callback -
          // it's only present on the session at this moment. It gets persisted into
          // calendar_connections once a workspace is active (ensureCalendarConnectionFromAuth).
          stashOAuthProviderTokens(newSession);
        }

        // Update auth state based on event
        switch (event) {
          case 'SIGNED_IN':
            if (newSession) {
              // If we're already in the middle of an invite acceptance flow,
              // don't override the state – let the existing flow continue.
              if (authState === 'accepting_invitation') {
                break;
              }

              // Check hash to see if this was an invite-based sign-in
              const hash = window.location.hash;
              const hashParams = new URLSearchParams(hash.substring(1));
              const type = hashParams.get('type');

              if (type === 'invite') {
                setAuthState('accepting_invitation');
              } else if (getPendingOAuthInvite()) {
                setAuthState('accepting_invitation');
              } else {
                // Defer the MFA/AAL check OUT of this callback. supabase-js holds its internal auth
                // lock for the duration of an onAuthStateChange callback, so calling another auth
                // method here (getAuthenticatorAssuranceLevel → _useSession → _acquireLock) can't get
                // the lock and blocks until MFA_CHECK_TIMEOUT_MS fires - that was the multi-second
                // boot stall, paid on every page load. Running it on the next tick lets this callback
                // return and release the lock first, so the check resolves instantly.
                setTimeout(async () => {
                  if (!mounted) return;
                  try {
                    const aalData = await getAalWithTimeout();
                    if (aalData?.nextLevel === 'aal2' && aalData?.currentLevel === 'aal1') {
                      setAuthState('mfa_challenge');
                    } else {
                      setAuthState('checking_invitations');
                    }
                  } catch (e) {
                    if (!isMfaTimeoutError(e)) {
                      console.error('Error checking MFA level:', e);
                    }
                    setAuthState('checking_invitations');
                  }
                }, 0);
              }
            }
            break;
          case 'SIGNED_OUT':
            setAuthState('unauthenticated');
            break;
          case 'INITIAL_SESSION':
            // Initial session found - already handled in initializeAuth
            // Don't change state here to avoid race conditions
            break;
          case 'TOKEN_REFRESHED':
            // Session refreshed, stay in current state
            break;
          case 'USER_UPDATED':
            // User updated, stay in current state
            break;
          default:
            // For other events, don't change state
            break;
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      setError(null);
      setIsLoading(true);
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        setError(signInError.message);
        return { error: signInError };
      }

      return { error: null };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to sign in');
      setError(error.message);
      return { error: { message: error.message } as AuthError };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signUp = useCallback(async (email: string, password: string, fullName: string) => {
    try {
      setError(null);
      setIsLoading(true);

      // Call the auth-signup edge function which uses Resend for email delivery
      // This bypasses Supabase's built-in email rate limits
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const appUrl = getAppUrl();
      const response = await fetch(edgeFnUrl('auth-signup'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: anonKey,
          // Supabase Functions gateway requires a Bearer JWT even for public functions.
          // The anon key is a JWT so it satisfies the check; without this header the
          // gateway returns 401 UNAUTHORIZED_NO_AUTH_HEADER and the function code
          // never runs (user sees "Failed to sign up" fallback in the catch path).
          Authorization: `Bearer ${anonKey}`,
        },
        body: JSON.stringify({
          email,
          password,
          fullName,
          redirectTo: appUrl,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        const errorMessage = result.error || 'Failed to sign up';
        setError(errorMessage);
        return { error: { message: errorMessage } as AuthError };
      }

      return { error: null };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to sign up');
      setError(error.message);
      return { error: { message: error.message } as AuthError };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);
      const { error: signOutError } = await supabase.auth.signOut();
      
      if (signOutError) {
        setError(signOutError.message);
        throw signOutError;
      }

      setSession(null);
      setUser(null);
      setAuthState('unauthenticated');
    } catch (err) {
      console.error('Error signing out:', err);
      setError(err instanceof Error ? err.message : 'Failed to sign out');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    try {
      setError(null);
      setIsLoading(true);

      // Call the auth-reset-password edge function which uses Resend for email delivery
      // This bypasses Supabase's built-in email rate limits
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const appUrl = getAppUrl();
      const response = await fetch(edgeFnUrl('auth-reset-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: anonKey,
          Authorization: `Bearer ${anonKey}`,
        },
        body: JSON.stringify({
          email,
          redirectTo: `${appUrl}/reset-password`,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        const errorMessage = formatEdgeFunctionError(result);
        setError(errorMessage);
        return { error: { message: errorMessage } as AuthError };
      }

      return { error: null };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to reset password');
      setError(error.message);
      return { error: { message: error.message } as AuthError };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    try {
      setError(null);
      setIsLoading(true);
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        setError(updateError.message);
        return { error: updateError };
      }

      return { error: null };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to update password');
      setError(error.message);
      return { error: { message: error.message } as AuthError };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value: AuthContextType = {
    session,
    user,
    authState,
    isLoading,
    error,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    setAuthState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

